import { API_BASE_URL } from "../../constants/index"; // Base URL for the API
import HttpInterface from "../../helpers/HttpInterface"; // Helper class for making HTTP requests
import { CreateArticle, UpdateArticle } from "../../Models/Article";
import { defaultFetchParam, FetchParam } from "../../Models/FetchParam"; // Fetch parameters model and default values
import { buildAPIUrl } from "../../api/apiHelpers"; // Utility function to build the API URL with query parameters

export class ArticlesService {
  private httpProxy: HttpInterface; // Instance of the HttpInterface to make HTTP requests

  // Constructor initializes the httpProxy with the HttpInterface
  constructor() {
    this.httpProxy = new HttpInterface(); // Creates an instance of the HttpInterface to handle requests
  }

  /**
   * Fetches all articles from the logistic service API.
   *
   * @param params The query parameters for the API request. Defaults to `defaultFetchParam` if not provided.
   * @returns A promise that resolves with the API response containing all articles.
   */
  all(params: FetchParam = defaultFetchParam): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Builds the complete API URL using the base URL, endpoint, and parameters
    return this.httpProxy.fetch(
      buildAPIUrl(`${API_BASE_URL}/logistic-service/articles`, params),
      requestOptions
    );
  }

  /**
   * Fetches a specific article by its ID from the API.
   *
   * @param id The unique identifier of the article to fetch.
   * @returns A promise that resolves with the API response containing the article details.
   */
  get(id: string): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Constructs the URL for fetching a specific article by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/articles/${id}`,
      requestOptions
    );
  }

  /**
   * Fetches article images by its ID from the API.
   *
   * @param id The unique identifier of the article to fetch.
   * @returns A promise that resolves with the API response containing the article images.
   */
  getImages(id: string): Promise<any> {
    const requestOptions = {
      method: "GET",
    };
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/articles/${id}/images`,
      requestOptions
    );
  }

  /**
   * Creates a new article in the logistic service API.
   *
   * @param article The details of the article to create.
   * @returns A promise that resolves with the API response confirming the creation of the article.
   */
  create(article: CreateArticle): Promise<any> {
    const requestOptions = {
      method: "POST",
      body: JSON.stringify(article),
    };

    // Sends a POST request to create the new article
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/articles`,
      requestOptions
    );
  }

  /**
   * Updates an existing article in the logistic service API.
   *
   * @param article The updated details of the article.
   * @returns A promise that resolves with the API response confirming the update.
   */
  edit(article: UpdateArticle): Promise<any> {
    const requestOptions = {
      method: "PUT", // HTTP method for the request
      body: JSON.stringify(article), // Sends the updated article data as JSON in the request body
    };

    // Sends a PUT request to update the specific article by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/articles/${article.id}`,
      requestOptions
    );
  }

  /**
   * Deletes a article from the logistic service API.
   *
   * @param id The unique identifier of the article to delete.
   * @returns A promise that resolves with the API response confirming the deletion.
   */
  delete(id: string): Promise<any> {
    const requestOptions = {
      method: "DELETE", // HTTP method for the request
    };

    // Sends a DELETE request to remove the specific article by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/articles/${id}`,
      requestOptions
    );
  }

  /**
   * Fetches a list of distinct companies associated with logistic service articles.
   *
   * @returns {Promise<any>} A promise that resolves with an array of unique company names or identifiers.
   *
   * @example
   * service.articlesCompanies().then(companies => {
   *   console.log("Distinct companies:", companies);
   * });
   */
  articlesCompanies(): Promise<any> {
    const requestOptions = {
      method: "GET",
    };

    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/articles/companies/distinct`,
      requestOptions
    );
  }
}
